import React, { useState, useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { NotificationManager } from 'react-notifications'
import { useGetRosterQuery, useRegisterAllMutation, useDownloadRosterMutation, rosterApi } from '../store/rosterApi'
import { setRegisteredAll } from '../store/miscSlice'
import { callAPI } from 'Shared/helpers'

const t = (key, opts) => (!window.I18n ? key : window.I18n.t(key, opts))

const MAX_POLL_ATTEMPTS = 90

const RosterActionBar = ({ onOpenMasterRosterImportModal, onOpenMemberModal, countOfEvents, eventsUrl, addGroupLeaderUrl, groupLeaderLimitReached }) => {
  const dispatch = useDispatch()
  const filters = useSelector((state) => state.filters)
  const registeredAll = useSelector((state) => state.misc.registeredAll)
  const { data } = useGetRosterQuery(filters)
  const [registerAll, { isLoading: isRegistering }] = useRegisterAllMutation()
  const [downloadRoster] = useDownloadRosterMutation()
  const [isDownloading, setIsDownloading] = useState(false)
  const pollTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    }
  }, [])

  const handleImportFromSpreadsheet = (e) => {
    e.preventDefault()
    if (!window.filepicker) return
    const gon = window.gon || {}
    window.filepicker.setKey(gon.filepickerApiKey)
    window.filepicker.pick(
      {
        // As filepicker_file_defaults, in the camelCase keys the picker reads.
        storeLocation: 'S3',
        storeContainer: gon.filepickerStoreContainer,
        storeAccess: 'public',
        storePath: gon.filepickerStorePath,
        extensions: ['.xls', '.xlsx', '.csv'],
        multiple: false,
      },
      (file) => {
        callAPI(gon.importRosterSpreadsheetUrl, 'POST', {
          filepicker_url: file.url,
          attachment_link: file.key,
          attachment_size: file.size,
          attachment_name: file.filename,
        }).then((response) => {
          if (response && response.jobPath) window.location.href = response.jobPath
        }).catch(() => {
          NotificationManager.error(t('customer_groups.groups.members.import_spreadsheet.error'))
        })
      },
      (error) => {
        if (error && error.code !== 101) {
          NotificationManager.error(t('customer_groups.groups.members.import_spreadsheet.error'))
        }
      },
    )
  }

  const handleRegisterAll = async () => {
    const result = await registerAll()
    if (result.error) {
      NotificationManager.error(
        result.error?.error || result.error?.message || t('customer_groups.groups.members.register_all_error')
      )
    } else {
      NotificationManager.success(t('customer_groups.groups.members.register_all_success'))
      dispatch(setRegisteredAll())
      dispatch(rosterApi.util.updateQueryData('getRoster', filters, (draft) => {
        draft.members.forEach((member) => {
          if (member.status === 'unregistered') member.status = 'invited'
        })
        draft.unregistered_count = 0
      }))
    }
  }

  const failDownload = () => {
    NotificationManager.error(t('customer_groups.groups.members.download_roster_error'))
    setIsDownloading(false)
  }

  const pollJobCompletion = useCallback((jobId) => {
    let lastIndex = 0
    let attempts = 0
    const poll = async () => {
      if (attempts >= MAX_POLL_ATTEMPTS) {
        failDownload()
        return
      }
      attempts += 1
      try {
        const resp = await fetch(`/jobs/${jobId}/poll.json`)
        if (!resp.ok) {
          failDownload()
          return
        }
        const messages = await resp.json()
        for (let i = lastIndex; i < messages.length; i++) {
          const msg = messages[i]
          if (msg.type === 'job-update' && msg.value?.status === 'completed' && msg.value?.pdf_path) {
            window.location.href = msg.value.pdf_path
            setIsDownloading(false)
            return
          }
          if (msg.type === 'job-update' && msg.value?.status === 'errors') {
            failDownload()
            return
          }
        }
        lastIndex = messages.length
        pollTimeoutRef.current = setTimeout(poll, 2000)
      } catch (_err) {
        failDownload()
      }
    }
    poll()
  }, [])

  const handleDownloadRoster = async () => {
    setIsDownloading(true)
    const result = await downloadRoster()
    if (result.error) {
      failDownload()
      return
    }
    pollJobCompletion(result.data.job_id)
  }

  const unregisteredCount = data?.unregistered_count || 0
  // TODO: persist registeredAll with a TTL so a reload cannot re-offer "register all".

  return (
    <div className='col-xs-12 col-sm-7 col-md-8 customer-group-members-action-bar'>
      {addGroupLeaderUrl && (groupLeaderLimitReached ? (
        // The title sits on the wrapper because a disabled button swallows hover.
        <span
          className='customer-group-members-gl-disabled'
          title={t('customer_groups.groups.leaders.index.gl_limit_reached')}
        >
          <button type='button' className='btn customer-group-members-secondary-btn' disabled>
            <i className='fa-solid fa-user-plus' aria-hidden='true' />
            {t('customer_groups.groups.members.add_group_leader_button')}
          </button>
        </span>
      ) : (
        <button
          type='button'
          className='btn customer-group-members-secondary-btn'
          onClick={() => { window.location.href = addGroupLeaderUrl }}
        >
          <i className='fa-solid fa-user-plus' aria-hidden='true' />
          {t('customer_groups.groups.members.add_group_leader_button')}
        </button>
      ))}

      <a href={eventsUrl} className='btn customer-group-members-secondary-btn customer-group-members-events-badge'>
        <i className='fa-regular fa-calendar' />
        {t('customer_groups.groups.members.events_count', { count: countOfEvents })}
      </a>

      {unregisteredCount > 0 && !registeredAll && (
        <button
          type='button'
          className='btn customer-group-members-secondary-btn'
          onClick={handleRegisterAll}
          disabled={isRegistering}
        >
          {isRegistering ? (
            t('customer_groups.groups.members.register_all_loading')
          ) : (
            <>
              <i className='fa-regular fa-envelope' />
              {t('customer_groups.groups.members.register_all_button', { count: unregisteredCount })}
            </>
          )}
        </button>
      )}

      <div className='btn-group'>
        <button
          type='button'
          className='btn btn-add-golfers dropdown-toggle'
          data-toggle='dropdown'
          aria-haspopup='true'
          aria-expanded='false'
        >
          <i className='fa-regular fa-square-plus' />
          {t('customer_groups.groups.members.add_golfers_button')}&nbsp;<span className='caret' />
        </button>
        <ul className='dropdown-menu dropdown-menu-right'>
          <li>
            <a href='#' onClick={(e) => { e.preventDefault(); onOpenMemberModal() }}>
              <i className='fa-solid fa-user-plus' />
              {t('customer_groups.groups.members.dropdown.add_manually')}
            </a>
          </li>
          <li>
            <a href='#' onClick={(e) => { e.preventDefault(); onOpenMasterRosterImportModal() }}>
              <i className='fa-solid fa-user-group' />
              {t('customer_groups.groups.members.dropdown.import_from_master_roster')}
            </a>
          </li>
          <li>
            <a href='#' onClick={handleImportFromSpreadsheet}>
              <i className='fa-solid fa-file-import' />
              {t('customer_groups.groups.members.dropdown.import_from_spreadsheet')}
            </a>
          </li>
        </ul>
      </div>

      <button
        type='button'
        className='btn customer-group-members-secondary-btn'
        onClick={handleDownloadRoster}
        disabled={isDownloading}
      >
        {isDownloading ? (
          t('customer_groups.groups.members.download_roster_loading')
        ) : (
          <>
            <i className='fa-solid fa-download' />
            {t('customer_groups.groups.members.download_roster_button')}
          </>
        )}
      </button>
    </div>
  )
}

RosterActionBar.propTypes = {
  onOpenMasterRosterImportModal: PropTypes.func.isRequired,
  onOpenMemberModal: PropTypes.func.isRequired,
  countOfEvents: PropTypes.number.isRequired,
  eventsUrl: PropTypes.string.isRequired,
  addGroupLeaderUrl: PropTypes.string,
  groupLeaderLimitReached: PropTypes.bool,
}

export default RosterActionBar
